import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ApiKeyManagement.module.css';
import { apiKeyApi, Firm as ApiFirm } from '../../services/apiKeyApi';
import { roleApi, Role as ApiRole } from '../../services/roleApi';
import { firmApi } from '../../services/firmApi';

interface APIKey {
  APIKey_ID: string;
  Key: string; // Masked key
  firms: ApiFirm[];
  role: string[];
  description: string;
  created_at?: string;
  updated_at?: string;
  org?: string; // Deprecated
}

interface APIKeyCreateRequest {
  firm_ids: string[];
  role: string[];
  description: string;
}

interface APIKeyCreateResponse {
  APIKey_ID: string;
  Key: string; // Full key - only shown once
  Secret: string; // Full secret - only shown once
  firms: ApiFirm[];
  role: string[];
  description: string;
  created_at?: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
}

interface Firm {
  Firm_ID: string;
  name: string;
  abbrev: string;
  status: number;
}

const ApiKeyManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [availableFirms, setAvailableFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [loadingFirms, setLoadingFirms] = useState<boolean>(true);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showResultModal, setShowResultModal] = useState<boolean>(false);
  const [showScimGuideModal, setShowScimGuideModal] = useState<boolean>(false);
  const [createdKey, setCreatedKey] = useState<APIKeyCreateResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Form state
  const [formData, setFormData] = useState<APIKeyCreateRequest>({
    firm_ids: [],
    role: [],
    description: ''
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchApiKeys();
    fetchAvailableRoles();
    fetchAvailableFirms();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const data = await apiKeyApi.getAllApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to load API keys. Please try again later.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      setLoadingRoles(true);
      const rolesData = await roleApi.getAllRoles();
      // Convert API roles to component format
      const formattedRoles: Role[] = rolesData.map((role: ApiRole) => ({
        id: role.Roles_ID,
        name: role.name,
        description: role.description || ''
      }));
      setAvailableRoles(formattedRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles. Please try again later.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchAvailableFirms = async () => {
    try {
      setLoadingFirms(true);
      const firmsData = await firmApi.getFirmsLookup();
      // Filter only active firms (status = 1)
      const activeFirms = firmsData
        .filter(firm => firm.status === 1)
        .map(firm => ({
          ...firm,
          Firm_ID: firm.Firm_ID.toString()
        }));
      setAvailableFirms(activeFirms);
    } catch (error) {
      console.error('Error fetching firms:', error);
      toast.error('Failed to load firms. Please try again later.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoadingFirms(false);
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.firm_ids.length === 0) {
      toast.error('At least one firm must be selected', { position: "top-right" });
      return;
    }
    
    if (formData.role.length === 0) {
      toast.error('At least one role must be selected', { position: "top-right" });
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Description is required', { position: "top-right" });
      return;
    }

    try {
      const newApiKey = await apiKeyApi.createApiKey(formData);
      setCreatedKey(newApiKey);
      setShowCreateModal(false);
      setShowResultModal(true);
      
      // Reset form
      setFormData({
        firm_ids: [],
        role: [],
        description: ''
      });
      
      // Refresh the list
      fetchApiKeys();
      
      toast.success('API key created successfully!', {
        position: "top-right",
        autoClose: 3000
      });
      
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key. Please try again.', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDeleteApiKey = async (keyId: string, firms: ApiFirm[]) => {
    const firmNames = firms.map(f => f.name).join(', ');
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete the API key for "{firmNames}"?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await apiKeyApi.deleteApiKey(keyId);
                setApiKeys(prevKeys => prevKeys.filter(key => key.APIKey_ID !== keyId));
                toast.success('API key deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
              } catch (error) {
                console.error('Error deleting API key:', error);
                toast.error('Failed to delete API key. Please try again.', {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast
      }
    );
  };

  const handleRoleChange = (roleName: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      role: checked 
        ? [...prev.role, roleName]
        : prev.role.filter((r: string) => r !== roleName)
    }));
  };

  const handleFirmChange = (firmId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      firm_ids: checked 
        ? [...prev.firm_ids, firmId]
        : prev.firm_ids.filter((id: string) => id !== firmId)
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`, {
        position: "top-right",
        autoClose: 2000
      });
    }).catch(() => {
      toast.error(`Failed to copy ${label}`, {
        position: "top-right",
        autoClose: 2000
      });
    });
  };

  // Filter API keys based on search term
  const filteredApiKeys = apiKeys.filter(apiKey => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      apiKey.description.toLowerCase().includes(search) ||
      apiKey.APIKey_ID.toLowerCase().includes(search) ||
      apiKey.Key.toLowerCase().includes(search) ||
      apiKey.firms.some(firm => firm.name.toLowerCase().includes(search)) ||
      apiKey.role.some(role => role.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading API keys...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
            
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">API Key Management</li>
        </ol>
      </nav>

      <div className="mb-4">
        <h2 className="mb-1">API Key Management</h2>
        <p className="text-muted mb-0">
          <i className="bi bi-key me-2"></i>
          Manage API keys for SCIM provisioning and external integrations
        </p>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableHeaderTop}>
            <div className="d-flex align-items-center">
              <i className="bi bi-table me-2 text-primary"></i>
              <h5 className="mb-0">API Keys</h5>
            </div>
            <div className={styles.headerActions}>
              <span className="badge bg-primary rounded-pill">
                {filteredApiKeys.length} {filteredApiKeys.length === 1 ? 'Key' : 'Keys'}
              </span>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={fetchApiKeys}
                title="Refresh API keys"
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
              <button 
                className="btn btn-outline-info btn-sm"
                onClick={() => setShowScimGuideModal(true)}
              >
                <i className="bi bi-question-circle me-1"></i> SCIM Guide
              </button>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-circle me-2"></i>
                New API Key
              </button>
            </div>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="p-3 border-bottom bg-white">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="bi bi-search"></i>
            </span>
            <input 
              type="text"
              className="form-control"
              placeholder="Search by description, key, firm, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
        </div>
        
        {/* Desktop Table View */}
        <div className={styles.desktopTable}>
          <div className="table-responsive">
            <table className={`table ${styles.apiKeyTable}`}>
              <thead>
                <tr>
                  <th>
                    <i className="bi bi-key me-1"></i>
                    Key
                  </th>
                  <th>
                    <i className="bi bi-building me-1"></i>
                    Firms
                  </th>
                  <th>
                    <i className="bi bi-shield-lock me-1"></i>
                    Roles
                  </th>
                  <th>
                    <i className="bi bi-card-text me-1"></i>
                    Description
                  </th>
                  <th>
                    <i className="bi bi-calendar me-1"></i>
                    Created
                  </th>
                  <th>
                    <i className="bi bi-tools me-1"></i>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredApiKeys.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-5">
                      <div className={styles.emptyState}>
                        {searchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No API keys found</h5>
                            <p className="text-muted mb-3">
                              Try adjusting your search criteria.
                            </p>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => setSearchTerm('')}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Clear Search
                            </button>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No API keys configured</h5>
                            <p className="text-muted mb-3">
                              Create your first API key to get started.
                            </p>
                            <button 
                              className="btn btn-primary"
                              onClick={() => setShowCreateModal(true)}
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Create First API Key
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredApiKeys.map((apiKey, index) => (
                    <tr key={apiKey.APIKey_ID} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td className={styles.tableCell}>
                        <div className={styles.keyInfo}>
                          <code className={styles.keyPrefix}>{apiKey.Key}</code>
                          <small className={styles.keyId}>ID: {apiKey.APIKey_ID}</small>
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.rolesContainer}>
                          {apiKey.firms && apiKey.firms.length > 0 ? (
                            apiKey.firms.map((firm: ApiFirm) => (
                              <span key={firm.Firm_ID} className={styles.roleBadge} style={{backgroundColor: '#0d6efd'}}>
                                {firm.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted">No firms assigned</span>
                          )}
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.rolesContainer}>
                          {apiKey.role.map((role: string) => (
                            <span key={role} className={styles.roleBadge}>
                              {role}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.description}>{apiKey.description}</span>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.dateInfo}>
                          <div>{apiKey.created_at ? formatDate(apiKey.created_at) : 'Unknown'}</div>
                          <small className="text-muted">ID: {apiKey.APIKey_ID}</small>
                        </div>
                      </td>
                      <td className={`${styles.apiKeyTableCell} text-end`}>
                        <div className={styles.actionButtons}>
                          <button 
                            className="btn btn-sm btn-outline-danger" 
                            onClick={() => handleDeleteApiKey(apiKey.APIKey_ID, apiKey.firms)}
                            title="Delete API key"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className={styles.mobileCards}>
          {filteredApiKeys.length === 0 ? (
            <div className="text-center p-5">
              <div className={styles.emptyState}>
                {searchTerm ? (
                  <>
                    <i className="bi bi-search display-4 text-muted mb-3"></i>
                    <h5 className="text-muted">No API keys found</h5>
                    <p className="text-muted mb-3">
                      Try adjusting your search criteria.
                    </p>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Clear Search
                    </button>
                  </>
                ) : (
                  <>
                    <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                    <h5 className="text-muted">No API keys configured</h5>
                    <p className="text-muted mb-3">
                      Create your first API key to get started.
                    </p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Create First API Key
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            filteredApiKeys.map((apiKey) => (
              <div key={apiKey.APIKey_ID} className={styles.apiKeyCard}>
                <div className={styles.apiKeyCardHeader}>
                  <div className="mb-2">
                    <code className={styles.keyPrefix}>{apiKey.Key}</code>
                  </div>
                  <small className="text-muted d-block">ID: {apiKey.APIKey_ID}</small>
                </div>
                
                <div className={styles.apiKeyCardBody}>
                  <div className="mb-2">
                    <small className="text-muted">
                      <i className="bi bi-building me-1"></i>
                      Firms:
                    </small>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {apiKey.firms && apiKey.firms.length > 0 ? (
                        apiKey.firms.map((firm: ApiFirm) => (
                          <span key={firm.Firm_ID} className={styles.roleBadge} style={{backgroundColor: '#0d6efd'}}>
                            {firm.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted">No firms assigned</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted">
                      <i className="bi bi-shield-lock me-1"></i>
                      Roles:
                    </small>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {apiKey.role.map((role: string) => (
                        <span key={role} className={styles.roleBadge}>
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted">
                      <i className="bi bi-card-text me-1"></i>
                      Description:
                    </small>
                    <div>{apiKey.description}</div>
                  </div>
                  
                  <div>
                    <small className="text-muted">
                      <i className="bi bi-calendar me-1"></i>
                      Created:
                    </small>
                    <div>{apiKey.created_at ? formatDate(apiKey.created_at) : 'Unknown'}</div>
                  </div>
                </div>

                <div className={styles.apiKeyCardActions}>
                  <button 
                    className="btn btn-sm btn-outline-danger flex-fill" 
                    onClick={() => handleDeleteApiKey(apiKey.APIKey_ID, apiKey.firms)}
                  >
                    <i className="bi bi-trash-fill me-1"></i>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className={`modal fade show d-block ${styles.modalWrapper}`} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New API Key</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateApiKey}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Firms <span className="text-danger">*</span></label>
                    {loadingFirms ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading firms...</span>
                        </div>
                        <div className="mt-2 small text-muted">Loading firms...</div>
                      </div>
                    ) : availableFirms.length === 0 ? (
                      <div className="alert alert-warning">
                        No active firms available. Please add firms first.
                      </div>
                    ) : (
                      <div className={styles.rolesCheckboxContainer} style={{maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.75rem'}}>
                        {availableFirms.map(firm => (
                          <div key={firm.Firm_ID} className="form-check">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id={`firm-${firm.Firm_ID}`}
                              checked={formData.firm_ids.includes(firm.Firm_ID)}
                              onChange={(e) => handleFirmChange(firm.Firm_ID, e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`firm-${firm.Firm_ID}`}>
                              <strong>{firm.name}</strong>
                              {firm.abbrev && (
                                <small className="text-muted d-block">Abbreviation: {firm.abbrev}</small>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Roles <span className="text-danger">*</span></label>
                    {loadingRoles ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading roles...</span>
                        </div>
                        <div className="mt-2 small text-muted">Loading roles...</div>
                      </div>
                    ) : availableRoles.length === 0 ? (
                      <div className="alert alert-warning">
                        No roles available. Please contact an administrator.
                      </div>
                    ) : (
                      <div className={styles.rolesCheckboxContainer}>
                        {availableRoles.map(role => (
                          <div key={role.id} className="form-check">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id={`role-${role.id}`}
                              checked={formData.role.includes(role.name)}
                              onChange={(e) => handleRoleChange(role.name, e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`role-${role.id}`}>
                              {role.name}
                              {role.description && (
                                <small className="text-muted d-block">{role.description}</small>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">Description <span className="text-danger">*</span></label>
                    <textarea 
                      className="form-control" 
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      required
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    Create API Key
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* API Key Created Result Modal */}
      {showResultModal && createdKey && (
        <div className={`modal fade show d-block ${styles.modalWrapper}`} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-success">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  API Key Created Successfully
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowResultModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Important:</strong> Please save these credentials securely. The secret will not be shown again!
                </div>
                
                <div className="mb-3">
                  <label className="form-label fw-bold">🔑 API Key:</label>
                  <div className={styles.credentialContainer}>
                    <code className={styles.credentialText}>{createdKey.Key}</code>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => copyToClipboard(createdKey.Key, 'API Key')}
                    >
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label fw-bold">🔒 Secret:</label>
                  <div className={styles.credentialContainer}>
                    <code className={styles.credentialText}>{createdKey.Secret}</code>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => copyToClipboard(createdKey.Secret, 'Secret')}
                    >
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <strong>Firms:</strong> {createdKey.firms.map(f => f.name).join(', ')}
                  </div>
                  <div className="col-md-6">
                    <strong>Roles:</strong> {createdKey.role.join(', ')}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>Description:</strong> {createdKey.description}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowResultModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SCIM Setup Guide Modal */}
      {showScimGuideModal && (
        <div className={`modal fade show d-block ${styles.modalWrapper}`} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-book me-2"></i>
                  Azure AD SCIM Provisioning Setup Guide
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowScimGuideModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className={styles.scimGuideContainer}>
                  
                  {/* Introduction */}
                  <div className="alert alert-info mb-4">
                    <h6 className="alert-heading">
                      <i className="bi bi-info-circle me-2"></i>
                      Overview
                    </h6>
                    <p className="mb-0">
                      This guide will walk you through setting up automatic user provisioning from Microsoft Entra ID (Azure AD) 
                      to your application using the SCIM 2.0 protocol.
                    </p>
                  </div>

                  {/* Step 1: Create Enterprise Application */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>1</span>
                      Create a New Enterprise Application in Azure Portal
                    </h5>
                    <ol className={styles.stepList}>
                      <li>Navigate to the <strong>Azure Portal</strong> (<code>portal.azure.com</code>)</li>
                      <li>Go to <strong>Microsoft Entra ID</strong> (formerly Azure Active Directory)</li>
                      <li>Select <strong>Enterprise Applications</strong> from the left menu</li>
                      <li>Click <strong>+ New application</strong></li>
                      <li>Click <strong>+ Create your own application</strong></li>
                      <li>Enter a name for your application (e.g., "YPOG SpeedDating")</li>
                      <li>Select <strong>"Integrate any other application you don't find in the gallery (Non-gallery)"</strong></li>
                      <li>Click <strong>Create</strong></li>
                    </ol>
                  </div>

                  {/* Step 2: Configure Provisioning */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>2</span>
                      Enable and Configure Provisioning
                    </h5>
                    <ol className={styles.stepList}>
                      <li>In your newly created Enterprise Application, navigate to <strong>Provisioning</strong> in the left menu</li>
                      <li>Click <strong>Get started</strong></li>
                      <li>Set <strong>Provisioning Mode</strong> to <strong>Automatic</strong></li>
                    </ol>
                  </div>

                  {/* Step 3: Admin Credentials */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>3</span>
                      Configure Admin Credentials
                    </h5>
                    <div className="alert alert-warning mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Important:</strong> You'll need to create an API Key first using the "New API Key" button above.
                    </div>
                    <ol className={styles.stepList}>
                      <li>In the <strong>Admin Credentials</strong> section, enter:
                        <ul>
                          <li><strong>Tenant URL:</strong> <code>{window.location.origin}/scim/v2</code>
                            <button 
                              className="btn btn-sm btn-outline-primary ms-2"
                              onClick={() => copyToClipboard(`${window.location.origin}/scim/v2`, 'SCIM URL')}
                            >
                              <i className="bi bi-clipboard"></i> Copy URL
                            </button>
                          </li>
                          <li><strong>Secret Token:</strong> Use the API Key and Secret you created (format: <code>APIKey.Secret</code>)</li>
                        </ul>
                      </li>
                      <li>Click <strong>Test Connection</strong> to verify the credentials</li>
                      <li>Once the connection test succeeds, click <strong>Save</strong></li>
                    </ol>
                  </div>

                  {/* Step 4: Configure Mappings */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>4</span>
                      Configure Attribute Mappings
                    </h5>
                    <ol className={styles.stepList}>
                      <li>Expand the <strong>Mappings</strong> section</li>
                      <li>Click on <strong>Provision Microsoft Entra ID Users</strong></li>
                      <li>Configure the following settings:
                        <div className={styles.configBox}>
                          <table className="table table-sm table-bordered mt-2">
                            <tbody>
                              <tr>
                                <th>Name</th>
                                <td>Provision Microsoft Entra ID Users</td>
                              </tr>
                              <tr>
                                <th>Enabled</th>
                                <td><span className="badge bg-success">Yes</span></td>
                              </tr>
                              <tr>
                                <th>Source Object</th>
                                <td>User</td>
                              </tr>
                              <tr>
                                <th>Source Object Scope</th>
                                <td>All records</td>
                              </tr>
                              <tr>
                                <th>Target Object</th>
                                <td><code>urn:ietf:params:scim:schemas:extension:enterprise:2.0:User</code></td>
                              </tr>
                              <tr>
                                <th>Target Object Actions</th>
                                <td>
                                  <span className="badge bg-primary me-1">Create</span>
                                  <span className="badge bg-warning me-1">Update</span>
                                  <span className="badge bg-danger">Delete</span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </li>
                    </ol>
                  </div>

                  {/* Step 5: Attribute Mappings Table */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>5</span>
                      Set Up Attribute Mappings
                    </h5>
                    <p className="mb-3">Configure the following attribute mappings. Click <strong>Add New Mapping</strong> for each custom attribute:</p>
                    
                    <div className="table-responsive" style={{maxHeight: '500px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.5rem'}}>
                      <table className="table table-hover table-bordered mb-0" style={{fontSize: '0.9rem'}}>
                        <thead style={{position: 'sticky', top: 0, zIndex: 10}}>
                          <tr style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                            <th style={{width: '40%', color: 'white', fontWeight: '600'}}>Target Attribute (Application)</th>
                            <th style={{width: '50%', color: 'white', fontWeight: '600'}}>Source Attribute (Azure AD)</th>
                            <th style={{width: '10%', color: 'white', fontWeight: '600', textAlign: 'center'}}>Matching</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{backgroundColor: '#fff3cd'}}>
                            <td><code style={{backgroundColor: '#ffe69c', padding: '2px 6px', borderRadius: '3px', fontWeight: '600'}}>userName</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>userPrincipalName</code></td>
                            <td style={{textAlign: 'center'}}>
                              <span className="badge" style={{background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)', color: 'white', fontSize: '0.85rem'}}>1</span>
                            </td>
                          </tr>
                          <tr>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>active</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>Switch([IsSoftDeleted], , "False", "True", "True", "False")</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>name.givenName</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>givenName</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>name.familyName</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>surname</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:jobtitle</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>jobTitle</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:company</code></td>
                            <td><code style={{backgroundColor: '#d4edda', padding: '2px 6px', borderRadius: '3px', fontWeight: '600'}}>YPOG GmbH & Co. KG</code> <span className="badge bg-success" style={{fontSize: '0.7rem'}}>constant</span></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:location</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>Switch([city], [city], "Köln", "Cologne", "München", "Munich")</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:gender</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>Switch([extensionAttribute10], "", "Männlich", "Male", "Weiblich", "Female")</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:linkedin</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.75rem'}}>Replace([extensionAttribute6], , ".*LinkedIn:([^;\\s][^;]*).*|.*", , "$1", , )</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:profile</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', fontSize: '0.7rem', display: 'block', wordBreak: 'break-all'}}>IIF(IsNullOrEmpty(Replace([extensionAttribute6], , ".*HubSpotProfile:([^;\\s][^;]*).*|.*", , "$1", , )), "", Join("", "https://ypog.law/team/", Replace([extensionAttribute6], , ".*HubSpotProfile:([^;\\s][^;]*).*", , "$1", , )))</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:employee_group</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>extensionAttribute8</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:department</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>department</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                          <tr style={{backgroundColor: '#e7f3ff'}}>
                            <td><code style={{backgroundColor: '#cfe2ff', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem'}}>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:title</code></td>
                            <td><code style={{backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px'}}>extensionAttribute1</code></td>
                            <td style={{textAlign: 'center'}}>-</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="alert alert-info mt-3">
                      <h6 className="alert-heading">
                        <i className="bi bi-lightbulb me-2"></i>
                        Tips for Adding Mappings
                      </h6>
                      <ul className="mb-0">
                        <li><strong>Matching Precedence:</strong> The <code>userName</code> mapping (highlighted in yellow) with precedence "1" is used to match existing users</li>
                        <li><strong>Custom Attributes:</strong> All attributes with blue background starting with <code>urn:ietf:params:scim:schemas:extension:ypog:2.0:User:</code> are custom YPOG extensions</li>
                        <li><strong>Expression Syntax:</strong> Copy the expressions exactly as shown above, including all brackets and quotes</li>
                        <li><strong>Constant Values:</strong> For the company field (green background), select "Constant" as the mapping type and enter <code>YPOG GmbH & Co. KG</code> directly</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 6: Configure Scoping */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>6</span>
                      Configure Scoping Filters (Optional)
                    </h5>
                    <ol className={styles.stepList}>
                      <li>In the mapping configuration, scroll to <strong>Source Object Scope</strong></li>
                      <li>You can choose to sync:
                        <ul>
                          <li><strong>All records:</strong> All users in your Azure AD</li>
                          <li><strong>Assigned users and groups:</strong> Only users/groups assigned to this enterprise application</li>
                        </ul>
                      </li>
                      <li>Click <strong>Save</strong> to apply the mappings</li>
                    </ol>
                  </div>

                  {/* Step 7: Assign Users */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>7</span>
                      Assign Users and Groups
                    </h5>
                    <ol className={styles.stepList}>
                      <li>Go back to your Enterprise Application overview</li>
                      <li>Select <strong>Users and groups</strong> from the left menu</li>
                      <li>Click <strong>+ Add user/group</strong></li>
                      <li>Select the users or groups you want to provision</li>
                      <li>Click <strong>Assign</strong></li>
                    </ol>
                  </div>

                  {/* Step 8: Start Provisioning */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>8</span>
                      Start Provisioning
                    </h5>
                    <ol className={styles.stepList}>
                      <li>Navigate back to <strong>Provisioning</strong></li>
                      <li>Click <strong>Start provisioning</strong></li>
                      <li>Azure AD will begin the initial synchronization cycle</li>
                      <li>Monitor the provisioning status in the <strong>Provisioning logs</strong></li>
                    </ol>
                    
                    <div className="alert alert-success mt-3">
                      <h6 className="alert-heading">
                        <i className="bi bi-check-circle me-2"></i>
                        Provisioning Cycles
                      </h6>
                      <p className="mb-0">
                        <strong>Initial Cycle:</strong> Can take 20-40 minutes depending on the number of users<br/>
                        <strong>Incremental Cycles:</strong> Run approximately every 40 minutes to sync changes
                      </p>
                    </div>
                  </div>

                  {/* Step 9: Monitoring */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <span className={styles.stepNumber}>9</span>
                      Monitor and Troubleshoot
                    </h5>
                    <ol className={styles.stepList}>
                      <li>View <strong>Provisioning logs</strong> to see detailed sync activity</li>
                      <li>Check for any errors or warnings in the logs</li>
                      <li>Use the <strong>Provisioning status</strong> to see overall health</li>
                      <li>Common issues:
                        <ul>
                          <li>Missing required attributes in Azure AD user profiles</li>
                          <li>Incorrect attribute mapping expressions</li>
                          <li>Permission issues with the API credentials</li>
                        </ul>
                      </li>
                    </ol>
                  </div>

                  {/* Additional Resources */}
                  <div className={styles.guideSection}>
                    <h5 className={styles.sectionTitle}>
                      <i className="bi bi-link-45deg me-2"></i>
                      Additional Resources
                    </h5>
                    <ul className={styles.resourceList}>
                      <li>
                        <a href="https://learn.microsoft.com/en-us/entra/identity/app-provisioning/user-provisioning" target="_blank" rel="noopener noreferrer">
                          Microsoft Entra ID Provisioning Documentation
                          <i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://learn.microsoft.com/en-us/entra/identity/app-provisioning/customize-application-attributes" target="_blank" rel="noopener noreferrer">
                          Customizing Attribute Mappings
                          <i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://learn.microsoft.com/en-us/entra/identity/app-provisioning/functions-for-customizing-application-data" target="_blank" rel="noopener noreferrer">
                          Expression Functions Reference
                          <i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                      </li>
                    </ul>
                  </div>

                </div>
              </div>
              <div className="modal-footer" style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderTop: '1px solid #dee2e6', borderRadius: '0 0 12px 12px', padding: '1.5rem'}}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowScimGuideModal(false)}
                  style={{borderRadius: '8px', fontWeight: '500', padding: '0.625rem 1.5rem'}}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ApiKeyManagement;
